import { useQueryClient } from '@tanstack/react-query';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

import type {
  GmailSendPayload,
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isGmailSendPayload } from '../../lib/proposals/core/types';
import { useDiscussionConversation } from '../../lib/proposals/hooks/useDiscussionConversation';
import { proposalQueryKeys } from '../../lib/proposals/query/keys';
import { isActionTypeSupported } from '../../lib/proposals/registry';
import {
  getPrimaryAction,
  isSameLogicalAction,
} from '../../lib/proposals/utils/helpers';
import { ActionRenderer } from './ActionRenderer';
import { useProposalRefresh } from './useProposalRefresh';
import VersionHistorySidebar from './VersionHistorySidebar';
import VersionItem from './VersionItem';
import VersionPreviewModal from './VersionPreviewModal';
/**
 * Skeleton for email action loading state.
 * Matches the layout of EmailActionPreview (To/Subject/Body).
 */
function EmailActionSkeleton() {
  return (
    <div className='mb-4 animate-pulse'>
      {/* Email header area */}
      <div className='rounded-xl border border-border-secondary bg-bg-primary p-5'>
        {/* Toolbar skeleton */}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <div className='h-7 w-7 bg-bg-tertiary rounded' />
            <div className='h-7 w-7 bg-bg-tertiary rounded' />
            <div className='h-7 w-7 bg-bg-tertiary rounded' />
          </div>
          <div className='h-5 w-20 bg-bg-tertiary rounded' />
        </div>
        {/* To field */}
        <div className='flex items-center gap-2 py-2.5 border-b border-border-secondary'>
          <div className='h-3.5 w-6 bg-bg-tertiary rounded' />
          <div className='h-5 w-40 bg-bg-tertiary rounded-full' />
        </div>
        {/* Subject field */}
        <div className='flex items-center gap-2 py-2.5 border-b border-border-secondary'>
          <div className='h-3.5 w-12 bg-bg-tertiary rounded' />
          <div className='h-4 w-56 bg-bg-tertiary rounded' />
        </div>
        {/* Body area */}
        <div className='pt-4 space-y-2.5'>
          <div className='h-3.5 w-full bg-bg-tertiary rounded' />
          <div className='h-3.5 w-full bg-bg-tertiary rounded' />
          <div className='h-3.5 w-4/5 bg-bg-tertiary rounded' />
          <div className='h-3.5 w-full bg-bg-tertiary rounded' />
          <div className='h-3.5 w-3/5 bg-bg-tertiary rounded' />
        </div>
      </div>
    </div>
  );
}

interface ActionVersionTimelineProps {
  action: ProposalDetail;
  onUpdate: (action: ProposalDetail) => void;
  isProcessing?: boolean;
  /** Callback when user clicks "View Thread" - opens the thread panel */
  onOpenThread?: () => void;
  /** Optimistic message to show immediately when user submits chat */
  optimisticMessage?: string | null;
  /** Functional update callback for non-email actions - avoids stale closure issues */
  onActionUpdate?: (
    actionUpdater: (action: ProposedAction) => ProposedAction | null
  ) => void;
  /** When true, the proposal has exactly one action; lifts the email body 300px cap. */
  isSoleAction?: boolean;
}

export interface ActionVersionTimelineRef {
  reload: () => void;
}

const ActionVersionTimeline = forwardRef<
  ActionVersionTimelineRef,
  ActionVersionTimelineProps
>(
  (
    {
      action,
      onUpdate,
      isProcessing = false,
      onOpenThread,
      // Kept on the props interface for callers that still pass it, but the
      // inline reasoning-chain bubble that consumed it has been removed.
      // The AgentResponsePanel above the StickyFooter shows live activity now.
      optimisticMessage: _optimisticMessage,
      onActionUpdate,
      isSoleAction = false,
    },
    ref
  ) => {
    const primaryAction = getPrimaryAction(action);
    const orgId = action.org;
    const proposalId = action.id;

    const [versions, setVersions] = useState<
      ProposedAction<GmailSendPayload>[]
    >([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(true); // Start true for initial load
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const handleVersionHistoryOpenChange = useCallback((open: boolean) => {
      if (!open) {
        setShowVersionHistory(false);
      }
    }, []);
    const [previewAction, setPreviewAction] =
      useState<ProposedAction<GmailSendPayload> | null>(null);

    // Auto-scroll refs
    const latestActionRef = useRef<HTMLDivElement>(null);
    const previousProposalIdRef = useRef<number | undefined>(undefined);
    const previousVersionCountRef = useRef<number>(0);

    // Subscribe to refresh events from ProposalRefreshProvider
    const { onRefresh } = useProposalRefresh();

    // This card's own action, read from a ref inside the refresh subscription so
    // the callback sees the current value without the effect re-subscribing on
    // every render.
    const ownActionRef = useRef<ProposedAction | undefined>(primaryAction);
    ownActionRef.current = primaryAction;

    const { isLoading: isLoadingDiscussion } = useDiscussionConversation(
      orgId,
      action.discussion_conversation
    );
    const queryClient = useQueryClient();

    // ID-based fetcher for versions - STABLE dependencies, no closure capture of primaryAction!
    // This is used by the onRefresh subscription to avoid stale closure issues
    const fetchVersionsForAction = useCallback(
      async (actionId: number, showLoading = false) => {
        if (!actionId) {
          return;
        }
        if (showLoading) {
          setIsLoadingVersions(true);
        }

        try {
          const url = `/api/orgs/${orgId}/proposals/${proposalId}/actions/${actionId}/versions/`;
          const response = await fetchWithDefaults(url);

          if (response.ok) {
            const data = await response.json();
            const validVersions = (data.results ||
              data) as ProposedAction<GmailSendPayload>[];
            // Sort versions chronologically (oldest first)
            const sortedVersions = [...validVersions].sort((a, b) => {
              const timeA = new Date(a.created_at || 0).getTime();
              const timeB = new Date(b.created_at || 0).getTime();
              return timeA - timeB;
            });
            setVersions(sortedVersions);
          }
        } catch (error) {
          console.error(
            '[ActionVersionTimeline] fetchVersionsForAction error:',
            error
          );
        } finally {
          setIsLoadingVersions(false);
        }
      },
      [orgId, proposalId]
    );

    // Fetch version list metadata
    const fetchVersions = useCallback(
      async (showLoading = false) => {
        if (!primaryAction) {
          return;
        }

        if (showLoading) {
          setIsLoadingVersions(true);
        }

        try {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/proposals/${proposalId}/actions/${primaryAction.id}/versions/`
          );

          if (response.ok) {
            const data = await response.json();
            // The /versions/ endpoint now returns full action data with payloads
            const validVersions = (data.results ||
              data) as ProposedAction<GmailSendPayload>[];

            // Add the current version (primaryAction) if it's not already in the list
            // The /versions/ endpoint often only returns historical versions
            const currentVersionInList = validVersions.some(
              v => v.id === primaryAction.id
            );
            const allVersions = currentVersionInList
              ? validVersions
              : [
                  ...validVersions,
                  primaryAction as ProposedAction<GmailSendPayload>,
                ];

            // Sort versions chronologically (oldest first) before grouping
            // This ensures actions within groups are in correct order
            const sortedVersions = [...allVersions].sort((a, b) => {
              const timeA = new Date(a.created_at || 0).getTime();
              const timeB = new Date(b.created_at || 0).getTime();
              return timeA - timeB;
            });

            setVersions(sortedVersions);
          }
        } catch (error) {
          console.error('Failed to fetch action versions:', error);
        } finally {
          setIsLoadingVersions(false);
        }
      },
      [primaryAction, proposalId, orgId]
    );

    // Load data on mount and when key IDs or version changes
    // Use stable IDs instead of callback functions to prevent re-fetching on every render
    const primaryActionId = primaryAction?.id;
    const discussionConversationId = action.discussion_conversation;
    // Also watch version number to trigger refetch when action payload is updated
    const primaryActionVersion = primaryAction?.version_number;

    // Hydrate from embedded versions if the proposal payload included them.
    // This avoids the per-action /versions/ fan-out on first paint.
    useEffect(() => {
      const embedded = primaryAction?.versions;
      if (embedded && embedded.length > 0) {
        const sorted = [...embedded].sort((a, b) => {
          const timeA = new Date(a.created_at || 0).getTime();
          const timeB = new Date(b.created_at || 0).getTime();
          return timeA - timeB;
        });
        setVersions(sorted as ProposedAction<GmailSendPayload>[]);
        setIsLoadingVersions(false);
        setHasInitiallyLoaded(true);
      }
    }, [primaryAction?.id, primaryAction?.versions]);

    useEffect(() => {
      const loadData = async () => {
        if (primaryAction?.versions && primaryAction.versions.length > 0) {
          // Embedded versions hydrated by the effect above — skip initial fetch.
          if (!hasInitiallyLoaded) {
            setHasInitiallyLoaded(true);
          }
          return;
        }
        const showLoading = !hasInitiallyLoaded;
        await fetchVersions(showLoading);
        if (!hasInitiallyLoaded) {
          setHasInitiallyLoaded(true);
        }
      };
      loadData();
      // Re-fetch when IDs change or version changes (e.g., after file upload)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      primaryActionId,
      discussionConversationId,
      proposalId,
      primaryActionVersion,
    ]);

    // Subscribe to refresh events (e.g., after AI completes editing)
    // IMPORTANT: Uses fresh data passed directly from the callback to avoid stale closures!
    //
    // Every action card mounts its OWN ActionVersionTimeline, and each one used
    // to read `getPrimaryAction(freshProposal)` here — the proposal's first
    // action, identical for all of them. So an N-action proposal fired N
    // identical `versions/` + `conversations/` requests on every refresh and
    // propagated the same proposal upward N times. Proposal-scoped work
    // (discussion fetch, upward propagation) is now done once, by the card that
    // owns the primary action; version history is fetched only by cards that
    // actually render it (the gmail_send branch below).
    useEffect(() => {
      return onRefresh(freshProposal => {
        if (!freshProposal) {
          return;
        }

        const ownAction = ownActionRef.current;
        const freshPrimaryAction = getPrimaryAction(freshProposal);
        const ownsProposalScope =
          !!ownAction &&
          !!freshPrimaryAction &&
          isSameLogicalAction(ownAction, freshPrimaryAction);

        // Version history only renders for gmail_send; fetching it for any
        // other card is pure waste. Resolve THIS card's action in the
        // refreshed proposal rather than assuming it is the primary one.
        if (ownAction && isGmailSendPayload(ownAction.payload)) {
          const freshOwnAction = (freshProposal.actions ?? []).find(candidate =>
            isSameLogicalAction(ownAction, candidate)
          );
          if (freshOwnAction) {
            fetchVersionsForAction(freshOwnAction.id, false);
          }
        }

        if (!ownsProposalScope) {
          // Non-primary cards intentionally don't call onUpdate here. Every
          // refresh already invalidates + refetches the exact React Query key
          // that ProposalDetailPanel's useProposal(action.org, action.id)
          // subscribes to (proposalQueryKeys.detail), so `currentAction` there
          // updates and re-renders the whole action list — including this
          // card's own action — through the normal prop flow. Calling
          // onUpdate from every card would be redundant, not a safety net.
          return;
        }

        if (freshProposal.discussion_conversation != null) {
          queryClient.invalidateQueries({
            queryKey: proposalQueryKeys.discussionConversation(
              orgId,
              freshProposal.discussion_conversation
            ),
          });
        }

        // Propagate fresh data to parent to update the displayed primaryAction
        // This fixes sidebar not updating after AI chat modifies actions
        onUpdate(freshProposal);
      });
    }, [onRefresh, fetchVersionsForAction, onUpdate, queryClient, orgId]);

    // Expose reload method - forcefully refetches without depending on memoized callbacks
    useImperativeHandle(
      ref,
      () => ({
        reload: async () => {
          await fetchVersions(true);
          if (action.discussion_conversation != null) {
            await queryClient.invalidateQueries({
              queryKey: proposalQueryKeys.discussionConversation(
                orgId,
                action.discussion_conversation
              ),
            });
          }
          setPreviewAction(null);
        },
      }),
      [fetchVersions, queryClient, action.discussion_conversation, orgId]
    );

    // Track proposal and version changes (without auto-scrolling).
    // Auto-scroll was removed because it caused jarring scroll jumps on mobile,
    // especially when opening proposals with email editors in the sidebar.
    useEffect(() => {
      previousProposalIdRef.current = proposalId;
      previousVersionCountRef.current = versions.length;
    }, [versions, proposalId]);

    // Handle action update from EmailActionPreview (must be before early returns to satisfy Rules of Hooks)
    const handleActionUpdate = useCallback(
      (updatedAction: ProposedAction<GmailSendPayload>) => {
        // When a new version is created, the ID changes (and so can the
        // execution_order — the repository renumbers on rollover). Match by
        // version tree, or by ID for an in-place update.
        const currentActions = action.actions || [];

        const mappedActions = currentActions.map(a =>
          isSameLogicalAction(a, updatedAction) ? updatedAction : a
        );

        const adopted = mappedActions.some(a => a === updatedAction);
        if (!adopted) {
          console.error(
            '[ActionVersionTimeline] Saved action matched no held row (stale-id risk)',
            {
              updatedActionId: updatedAction.id,
              rootId: updatedAction.parent_action ?? updatedAction.id,
            }
          );
        }

        onUpdate({ ...action, actions: mappedActions });
        fetchVersions();
      },
      [action, onUpdate, fetchVersions]
    );

    // Handle restore version (must be before early returns to satisfy Rules of Hooks)
    const handleRestore = useCallback(
      async (actionToRestore: ProposedAction<GmailSendPayload>) => {
        if (!primaryAction) {
          return;
        }

        try {
          // Update current version with restored content
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/proposals/${proposalId}/actions/${primaryAction.id}/update_payload/`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                payload: actionToRestore.payload,
              }),
            }
          );

          if (response.ok) {
            // Endpoint returns a {action, proposal} envelope — unwrap to the action.
            const { action: updatedAction } = await response.json();

            // Update parent
            const updatedProposal = {
              ...action,
              actions: action.actions.map(a =>
                isSameLogicalAction(a, updatedAction) ? updatedAction : a
              ),
            };
            onUpdate(updatedProposal);

            // Reload versions
            fetchVersions();

            // Close modal
            setPreviewAction(null);
          } else {
            console.error('Failed to restore version');
          }
        } catch (error) {
          console.error('Failed to restore version:', error);
        }
      },
      [primaryAction, action, onUpdate, fetchVersions, proposalId, orgId]
    );

    if (!primaryAction) {
      return (
        <div className='p-6'>
          <p className='text-sm text-secondary'>No action to display</p>
        </div>
      );
    }

    // Handle non-email action types via ActionRenderer
    // Email actions continue to the version entity flow below
    if (!isGmailSendPayload(primaryAction.payload)) {
      // Check if this action type is supported
      if (!isActionTypeSupported(primaryAction)) {
        return (
          <div className='p-6'>
            <p className='text-sm text-secondary'>
              This action type is not supported for display
            </p>
          </div>
        );
      }

      // Handler for action updates from renderers
      const handleNonEmailActionUpdate = (updatedAction: ProposedAction) => {
        if (onActionUpdate) {
          // Use functional update pattern - no stale closure!
          onActionUpdate(a =>
            isSameLogicalAction(a, updatedAction) ? updatedAction : null
          );
          return;
        }

        // Fallback to existing behavior for backward compatibility
        const currentActions = action.actions || [];
        const mappedActions = currentActions.map(a =>
          isSameLogicalAction(a, updatedAction) ? updatedAction : a
        );

        const adopted = mappedActions.some(a => a === updatedAction);
        if (!adopted) {
          console.error(
            '[ActionVersionTimeline] Saved action matched no held row (stale-id risk)',
            {
              updatedActionId: updatedAction.id,
              rootId: updatedAction.parent_action ?? updatedAction.id,
            }
          );
        }

        onUpdate({ ...action, actions: mappedActions });
      };

      // Render via ActionRenderer (registry-based dispatch).
      // No outer margin — this is rendered inside an ActionAccordion's card
      // shell, which already controls trailing spacing. An mb-4 here showed
      // up as a 16px white bar at the bottom of every action card.
      return (
        <ActionRenderer
          action={primaryAction}
          mode={isProcessing ? 'readonly' : 'full'}
          onUpdate={handleNonEmailActionUpdate}
          proposalId={proposalId}
          orgId={orgId}
          isProcessing={isProcessing}
          sourceTypeName={action.source_type_name}
          sourceContext={action.source_context}
        />
      );
    }

    // For Gmail actions, continue with the full version entity flow

    const loading = isLoadingVersions || isLoadingDiscussion;

    // Only show skeleton on initial load when we don't have primaryAction content yet.
    // If we have primaryAction, render immediately - versions/discussion can load in background.
    // This eliminates the skeleton flash when navigating between actions.
    const hasPrimaryActionContent =
      primaryAction && isGmailSendPayload(primaryAction.payload);
    if (loading && !hasInitiallyLoaded && !hasPrimaryActionContent) {
      return <EmailActionSkeleton />;
    }

    // Check if we have version history (more than current version)
    const hasHistory = versions.length > 1;

    // The reasoning-chain chat bubble that used to render inline below the
    // action card has been removed — the AgentResponsePanel above the
    // StickyFooter is now the single home for "what the agent is doing /
    // saying right now". Rendering both was duplicative and made the action
    // card noisy during processing.

    return (
      // No outer margin — this is rendered inside an ActionAccordion's card
      // shell, which already handles trailing spacing. An mb-4 here showed
      // up as a 16px white bar at the bottom of the email action card.
      <div>
        {/* Version History Sidebar - Opens when toolbar history button is clicked */}
        <VersionHistorySidebar
          isOpen={showVersionHistory}
          onOpenChange={handleVersionHistoryOpenChange}
          versions={versions}
          onVersionClick={v =>
            setPreviewAction(v as ProposedAction<GmailSendPayload>)
          }
          currentVersionId={primaryAction?.id}
        />

        {/* Current Version (Always Visible) */}
        {/* Use primaryAction from props directly to ensure we always show latest data */}
        {primaryAction && isGmailSendPayload(primaryAction.payload) && (
          <div ref={latestActionRef}>
            <VersionItem
              actions={[primaryAction as ProposedAction<GmailSendPayload>]}
              isLatest={true}
              editable={!isProcessing}
              onActionUpdate={handleActionUpdate}
              onClick={setPreviewAction}
              isProcessing={isProcessing}
              orgId={orgId}
              proposalId={proposalId}
              onOpenThread={onOpenThread}
              versionCount={versions.length}
              onShowVersionHistory={
                hasHistory
                  ? () => setShowVersionHistory(!showVersionHistory)
                  : undefined
              }
              isSoleAction={isSoleAction}
            />
          </div>
        )}

        {/* Version Preview Modal */}
        {previewAction && (
          <VersionPreviewModal
            action={previewAction}
            proposalId={proposalId}
            isOpen={!!previewAction}
            onClose={() => setPreviewAction(null)}
            onRestore={handleRestore}
            isLatest={previewAction.is_current_version ?? false}
            orgId={orgId}
          />
        )}
      </div>
    );
  }
);

ActionVersionTimeline.displayName = 'ActionVersionTimeline';

export default ActionVersionTimeline;
